import type { ReactNode } from 'react'

import type { DataTableColumn, DataTableFilterValue, SortOrder } from './data-table.interface'

/**
 * Extracts the display value from a table row based on column configuration.
 * Supports custom cell renderers, accessor keys, and handles Date formatting.
 */
export const getCellValue = <TData>(row: TData, column: DataTableColumn<TData>): ReactNode => {
  if (column.cell) {
    return column.cell(row)
  }

  if (!column.accessorKey) {
    return null
  }

  const value = row[column.accessorKey]

  if (value instanceof Date) {
    return value.toLocaleDateString()
  }

  return String(value ?? '')
}

/**
 * Determines the next sort state when a column header is clicked.
 * Cycles through: none -> asc -> desc -> none
 */
export const getNextSortState = (
  columnId: string,
  currentSortBy: string | undefined,
  currentSortOrder: SortOrder | undefined
): { sortBy: string; sortOrder: SortOrder } => {
  if (currentSortBy !== columnId) {
    return { sortBy: columnId, sortOrder: 'asc' }
  }

  if (currentSortOrder === 'asc') {
    return { sortBy: columnId, sortOrder: 'desc' }
  }

  return { sortBy: '', sortOrder: 'asc' }
}

export const filterBySearch = <TData>(
  data: TData[],
  searchValue: string,
  searchKeyList: (keyof TData)[]
): TData[] => {
  if (!searchValue) return data

  const searchLower = searchValue.toLowerCase()

  return data.filter((item) =>
    searchKeyList.some((key) => {
      const value = item[key]
      return (
        value !== null && value !== undefined && String(value).toLowerCase().includes(searchLower)
      )
    })
  )
}

export const filterByFilters = <TData>(
  data: TData[],
  filterValueList: DataTableFilterValue[],
  filterKeyMap: Record<string, keyof TData>
): TData[] => {
  if (filterValueList.length === 0) return data

  return data.filter((item) => {
    for (const filterValue of filterValueList) {
      if (filterValue.valueList.length === 0) continue

      const key = filterKeyMap[filterValue.filterId]
      if (!key) continue

      const itemValue = item[key]
      if (!filterValue.valueList.includes(String(itemValue))) {
        return false
      }
    }
    return true
  })
}

export const sortData = <TData>(
  data: TData[],
  sortBy: keyof TData | string | undefined,
  sortOrder: SortOrder | undefined
): TData[] => {
  if (!sortBy) return data

  return [...data].sort((a, b) => {
    const aValue = a[sortBy as keyof TData]
    const bValue = b[sortBy as keyof TData]

    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1

    const comparison = String(aValue).localeCompare(String(bValue))
    return sortOrder === 'desc' ? -comparison : comparison
  })
}

export const paginateData = <TData>(data: TData[], page: number, pageSize: number): TData[] => {
  const startIndex = (page - 1) * pageSize
  return data.slice(startIndex, startIndex + pageSize)
}
